import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { map } from 'underscore';
import { withErrorField } from '@bingads-webui-react/primitive-validation-fluent';
import { withValidation, GenericAdapter } from '@bingads-webui-react/validation';
import { useBudgetRange } from '@bingads-webui-campaign-react/budget-component-v2';
import { Radio, RadioGroup, Text, makeStyles, shorthands } from '@fluentui/react-components';
import { AdditionalConversionInputPure } from './additional-conversion-input-pure';
import { ADDITIONAL_CONVERSION_VALUE_TYPES } from '../constants';

const AdditionalConversionInput = withErrorField(withValidation(AdditionalConversionInputPure, GenericAdapter));

const useAdditionalConversionInputStyle = makeStyles({
  additionalValueInputSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    '& .fui-Input': {
      maxWidth: '136px',
    },
  },
  radioGroupContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& input[type="radio"]': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& .fui-Radio__indicator': {
      height: '20px',
      width: '20px',
      ...shorthands.margin('0px'),
    },
    '& label': {
      ...shorthands.padding('0px', '0px', '0px', '8px'),
    },
  },
  radioOption: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& .fui-Field': {
      marginLeft: '28px',
    },
  },
});

export const ValidatedAdditionalConversionInput = (props) => {
  const {
    i18n, isAccountLevel, accountAdditionalConversionValue,
    conversionValueOption, setConversionValueOption,
    validationProps, ...rest
  } = props;
  const styles = useAdditionalConversionInputStyle();
  const {
    currencyCode,
    minDailyBudget,
    maxDailyBudget,
  } = useBudgetRange();

  const budgetSchema = useMemo(() => ({
    name: 'Budget schema',
    type: 'object',
    properties: {
      'account-setting': {
        type: 'number',
        required: true,
        maximum: maxDailyBudget,
        minimum: minDailyBudget,
      },
      'campaign-setting': {
        type: 'number',
        required: true,
        maximum: maxDailyBudget,
        minimum: minDailyBudget,
      },
    },
  }), [maxDailyBudget, minDailyBudget]);

  const customErrorProcessor = useCallback(errors => map(
    errors,
    (oldError, key) => {
      const customErrors = {
        decimalLength: i18n.getString(_TL_('Budget needs to be set to 2 decimal lengths')),
        range: i18n.getString(_TL_('Your budget should be greater than {{accountCurrencyCode}} {{dailyRangeFloorValue}} and less than your daily maximum. The daily max. is your monthly budget averaged out over the number of days left in the month.'), {
          accountCurrencyCode: currencyCode,
          dailyRangeFloorValue: minDailyBudget,
        }),
        required: i18n.getString(_TL_('This field is required')),
        type: i18n.getString(_TL_('Field requires numeric value')),
      };
      return customErrors[key] || oldError;
    }
  ), [i18n, currencyCode, minDailyBudget]);

  const renderAccountLevelSettings = useCallback(() => (
    <>
      <Text size={300}>
        {i18n.getString(_TL_('Enter the target amount you want to pay for a new customer’s purchase of your product or service.'))}
        <Text size={300} italic>{i18n.getString(_TL_(' Note: '))}</Text>
        {i18n.getString(_TL_('The final value will be based on the target amount you enter here plus the default purchase value set in your '))}
        <Text size={300} weight="bold">{i18n.getString(_TL_('Purchase'))}</Text>
        {i18n.getString(_TL_(' conversion goal.'))}
      </Text>
      <AdditionalConversionInput
        className="account-additional-value-input"
        size="medium"
        currencySymbol={i18n.getCurrencySymbol(currencyCode)}
        validationProps={{
          schema: budgetSchema,
          field: 'account-setting',
          errorProcessor: customErrorProcessor,
          ...validationProps,
        }}
        {...rest}
      />
    </>
  ), [budgetSchema, currencyCode, customErrorProcessor, i18n, rest, validationProps]);

  const onConversionValueOptionChange = useCallback((_, data) => {
    if (data) {
      setConversionValueOption(data.value);
    }
  }, [setConversionValueOption]);

  const renderCampaignLevelSettings = useCallback(() => (
    <>
      <RadioGroup
        className={styles.radioGroupContainer}
        value={conversionValueOption}
        onChange={onConversionValueOptionChange}
      >
        <Radio
          className="account-level-radio-option"
          value={ADDITIONAL_CONVERSION_VALUE_TYPES.ACCOUNT}
          label={
            <Text size={300} weight="regular">
              {i18n.getString(_TL_('Use account-level settings'))} {`(${i18n.formatCurrency(Number(accountAdditionalConversionValue), currencyCode)})`}
            </Text>
          }
        />
        <div className={styles.radioOption}>
          <Radio
            className="campaign-level-radio-option"
            value={ADDITIONAL_CONVERSION_VALUE_TYPES.CAMPAIGN}
            label={
              <Text size={300} weight="regular">
                {i18n.getString(_TL_('Use a different value for this campaign'))}
              </Text>
            }
          />
          { conversionValueOption === ADDITIONAL_CONVERSION_VALUE_TYPES.CAMPAIGN && (
            <AdditionalConversionInput
              className="campaign-additional-value-input"
              size="medium"
              currencySymbol={i18n.getCurrencySymbol(currencyCode)}
              validationProps={{
                schema: budgetSchema,
                field: 'campaign-setting',
                errorProcessor: customErrorProcessor,
                ...validationProps,
              }}
              {...rest}
            />
          )}
        </div>
      </RadioGroup>
    </>
  ), [styles.radioGroupContainer, styles.radioOption, conversionValueOption, onConversionValueOptionChange, i18n, accountAdditionalConversionValue, currencyCode, budgetSchema, customErrorProcessor, validationProps, rest]);

  return (
    <div className={styles.additionalValueInputSection}>
      <Text size={400} weight="bold">{i18n.getString(_TL_('Conversion value for new customers'))}</Text>
      { isAccountLevel ? renderAccountLevelSettings() : renderCampaignLevelSettings() }
    </div>
  );
};

ValidatedAdditionalConversionInput.propTypes = {
  accountAdditionalConversionValue: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    formatCurrency: PropTypes.func,
    getCurrencySymbol: PropTypes.func,
  }).isRequired,
  isAccountLevel: PropTypes.bool.isRequired,
  conversionValueOption: PropTypes.string.isRequired,
  setConversionValueOption: PropTypes.func.isRequired,
  setBudgetInput: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({}),
};

ValidatedAdditionalConversionInput.defaultProps = {
  validationProps: {},
};
